export interface AutoRouteRequest {
  markerId: string;
  // Date.now() + the maximum wait, taken when armed.
  deadline: number;
  seenOpen: boolean;
  phase: 'waiting' | 'drawn';
}

export interface AutoRouteContext {
  now: number;
  selectedMarkerId?: string;
  isOpen: boolean;
  markerPresent: boolean;
  ticketReady: boolean;
  routeShowing: boolean;
  // A function, so the status is only looked up when it is needed.
  statusAllowsRoute: () => boolean;
}

export type AutoRouteAction = 'wait' | 'end' | 'draw';

/**
 * Decides what to do with a pending automatic route request.
 * Pure, so the caller stores the returned seenOpen and acts
 * on the returned action.
 */
export function decideAutoRoute(
  request: Pick<AutoRouteRequest, 'markerId' | 'deadline' | 'seenOpen'>,
  context: AutoRouteContext,
): { action: AutoRouteAction; seenOpen: boolean } {
  const { seenOpen } = request;

  if (context.now > request.deadline) {
    return { action: 'end', seenOpen };
  }

  const otherMarkerSelected =
    !!context.selectedMarkerId && context.selectedMarkerId !== request.markerId;
  if (otherMarkerSelected) {
    return { action: 'end', seenOpen };
  }

  if (!context.isOpen) {
    // Seen open before means the popup was closed since: end.
    // Otherwise it is still loading.
    return { action: seenOpen ? 'end' : 'wait', seenOpen };
  }

  // A marker briefly missing during startup keeps the request
  // waiting until the deadline.
  if (!context.markerPresent) {
    return { action: 'wait', seenOpen: true };
  }

  if (!context.ticketReady) {
    return { action: 'wait', seenOpen: true };
  }

  // showPredictiveRoute toggles, so never call it for a route
  // that is already showing or whose status allows no route.
  if (context.routeShowing || !context.statusAllowsRoute()) {
    return { action: 'end', seenOpen: true };
  }

  return { action: 'draw', seenOpen: true };
}
